import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout'
import { Button } from '@progress/kendo-react-buttons'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { useToast } from '../../contexts/ToastContext'
import { FaFilePdf } from 'react-icons/fa'
import { getTargetJob } from '../../data/jobData'
import { getCurrentUserEmployee } from '../../data/employeeData'
import { JobVariation, TargetJobInvoiceItems } from '../../data/interfaces'
import '../Styles.css'
import './FinancialStatement.css'

interface Quote {
  quoteId: number
  ref: number
  refFormatted: string
  description?: string
  value?: string
  createdDate?: string
}

interface FinancialStatementProps {}

function FinancialStatement(props: FinancialStatementProps) {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [hasPermission, setHasPermission] = useState(false)
  const [jobReference, setJobReference] = useState('')
  const [quotes, setQuotes] = useState<Quote[]>([])
  const [variations, setVariations] = useState<JobVariation[]>([])
  const [invoices, setInvoices] = useState<TargetJobInvoiceItems[]>([])

  // Check permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const currentUser = await getCurrentUserEmployee()
        console.log('FinancialStatement - Current user:', currentUser)
        console.log('FinancialStatement - Current user permissions:', currentUser?.permissions)
        if (currentUser && currentUser.permissions) {
          const permitted = currentUser.permissions.some(
            (p) => p.area === 'Reports' && p.permission === 'FinancialStatement' && p.assigned
          )
          console.log('FinancialStatement - Has permission:', permitted)
          setHasPermission(permitted)
          if (!permitted) {
            showToast('You do not have permission to access this page', 'error')
            navigate('/')
          }
        } else {
          console.warn('FinancialStatement - No permissions found for current user')
          showToast('Unable to verify permissions', 'error')
          navigate('/')
        }
      } catch (error) {
        console.error('FinancialStatement - Error checking permissions:', error)
        showToast('Error verifying permissions', 'error')
        navigate('/')
      }
    }
    checkPermissions()
  }, [navigate, showToast])

  // Fetch job details for the header
  useEffect(() => {
    if (!hasPermission) return
    
    const fetchJobDetails = async () => {
      try {
        setLoading(true)
        const jobData = await getTargetJob(Number(jobId))
        setJobReference(jobData.refFormatted)
        
        // Fetch full quote details
        const quoteIds = jobData.quotes || []
        const quotesWithDetails = await Promise.all(
          quoteIds.map(async (q) => {
            try {
              const response = await fetch(`https://${process.env.REACT_APP_API_URL}/Quotes/${q.quoteId}`, {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: 'Bearer ' + localStorage.getItem('authToken'),
                },
              })
              const quoteDetail = await response.json()
              return {
                quoteId: q.quoteId,
                ref: q.ref,
                refFormatted: q.refFormatted,
                description: quoteDetail.description,
                value: quoteDetail.value || quoteDetail.cost,
                createdDate: quoteDetail.createdDate
              }
            } catch (error) {
              console.error(`Error fetching quote ${q.quoteId}:`, error)
              return q
            }
          })
        )
        setQuotes(quotesWithDetails)
        
        setVariations(jobData.variations || [])
        
        // Calculate VAT for invoices using the xeroTaxRate already in invoiceItems
        const invoiceItems = jobData.invoiceItems || []
        const invoicesWithVat = invoiceItems.map((invoice) => {
          const taxRate = invoice.xeroTaxRate?.rate || 0
          const calculatedVat = (invoice.itemValue || 0) * (taxRate / 100)
          return {
            ...invoice,
            vatValue: calculatedVat
          }
        })
        setInvoices(invoicesWithVat)
      } catch (error) {
        console.error('Error fetching job details:', error)
        showToast('Error loading job details', 'error')
      } finally {
        setLoading(false)
      }
    }

    if (jobId) {
      fetchJobDetails()
    }
  }, [jobId, showToast, hasPermission])

  const handleBack = () => {
    navigate(-1)
  }

  const handleExportPDF = async () => {
    try {
      const authToken = localStorage.getItem('authToken')
      const response = await fetch(
        `https://${process.env.REACT_APP_API_URL}/PDF/FinancialStatement/${jobId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to generate PDF: ${response.statusText}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `FinancialStatement_${jobReference}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      showToast('PDF exported successfully', 'success')
    } catch (error) {
      console.error('Error exporting PDF:', error)
      showToast('Error exporting PDF', 'error')
    }
  }

  if (loading) {
    return (
      <div>
        <div style={{ padding: "1rem" }}>
          <div className="financial-statement__header">
            <h1>Financial Statement</h1>
          </div>
          <div style={{ 
            textAlign: 'center', 
            padding: '4rem 2rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div className="loading-spinner" style={{
              width: '50px',
              height: '50px',
              border: '5px solid #f3f3f3',
              borderTop: '5px solid #1e2a4e',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#1e2a4e', fontSize: '1.1rem', fontWeight: 500 }}>
              Loading financial statement...
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div style={{ padding: "1rem" }}>
        <div className="financial-statement__header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h1>Financial Statement - {jobReference}</h1>
            <Button
              onClick={handleExportPDF}
              icon="file-pdf"
              themeColor="primary"
            >
              <FaFilePdf style={{ marginRight: "0.5rem" }} />
              Export to PDF
            </Button>
          </div>
        </div>

        <TabStrip
          selected={activeTab}
          onSelect={(e) => setActiveTab(e.selected)}
          style={{ marginTop: '1rem' }}
        >
          <TabStripTab title="Quotes">
            <div style={{ padding: '1rem 0' }}>
              {quotes.length === 0 ? (
                <div style={{ 
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  <p>No quotes found for this job.</p>
                </div>
              ) : (
                <Grid
                  data={quotes}
                  style={{ height: 'auto' }}
                >
                  <GridColumn field="refFormatted" title="Quote Reference" width="180px" />
                  <GridColumn field="description" title="Description" width="350px" />
                  <GridColumn 
                    field="value" 
                    title="Value" 
                    width="150px"
                    cell={(props) => {
                      const value = props.dataItem.value
                      if (value === null || value === undefined) return <td>-</td>
                      const formatted = new Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: 'GBP'
                      }).format(value)
                      return <td>{formatted}</td>
                    }}
                  />
                  <GridColumn 
                    field="createdDate" 
                    title="Created Date" 
                    width="180px"
                    cell={(props) => {
                      const date = props.dataItem.createdDate
                      if (!date) return <td>-</td>
                      const formatted = new Date(date).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })
                      return <td>{formatted}</td>
                    }}
                  />
                  <GridColumn 
                    field="quoteId" 
                    title="Actions" 
                    width="150px"
                    cell={(props) => (
                      <td>
                        <Button
                          onClick={() => window.open(`/quotes/?id=${props.dataItem.quoteId}`, '_blank')}
                          themeColor="primary"
                        >
                          View Quote
                        </Button>
                      </td>
                    )}
                  />
                </Grid>
              )}
            </div>
          </TabStripTab>

          <TabStripTab title="Variations">
            <div style={{ padding: '1rem 0' }}>
              {variations.length === 0 ? (
                <div style={{ 
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  <p>No variations found for this job.</p>
                </div>
              ) : (
                <Grid
                  data={variations}
                  style={{ height: 'auto' }}
                >
                  <GridColumn field="description" title="Description" width="500px" />
                  <GridColumn 
                    field="cost" 
                    title="Cost" 
                    width="180px"
                    cell={(props) => {
                      const cost = props.dataItem.cost
                      const formatted = new Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: 'GBP'
                      }).format(cost || 0)
                      return <td>{formatted}</td>
                    }}
                  />
                  <GridColumn 
                    field="createdDate" 
                    title="Created Date" 
                    width="180px"
                    cell={(props) => {
                      const date = props.dataItem.createdDate
                      if (!date) return <td>-</td>
                      const formatted = new Date(date).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })
                      return <td>{formatted}</td>
                    }}
                  />
                </Grid>
              )}
            </div>
          </TabStripTab>

          <TabStripTab title="Invoices">
            <div style={{ padding: '1rem 0' }}>
              {invoices.length === 0 ? (
                <div style={{ 
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  <p>No invoices found for this job.</p>
                </div>
              ) : (
                <Grid
                  data={invoices}
                  style={{ height: 'auto' }}
                >
                  <GridColumn 
                    field="invoiceRef" 
                    title="Invoice No" 
                    width="180px"
                    cell={(props) => {
                      const ref = props.dataItem.invoiceRef
                      const formatted = ref ? `I${String(ref)}` : '-'
                      return <td>{formatted}</td>
                    }}
                  />
                  <GridColumn 
                    field="invoiceDate" 
                    title="Invoice Date" 
                    width="150px"
                    cell={(props) => {
                      const date = props.dataItem.invoiceDate
                      if (!date) return <td>-</td>
                      const formatted = new Date(date).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })
                      return <td>{formatted}</td>
                    }}
                  />
                  <GridColumn field="description" title="Description" />
                  <GridColumn field="department" title="Department" width="150px" />
                  <GridColumn 
                    field="itemValue" 
                    title="Value" 
                    width="120px"
                    cell={(props) => {
                      const value = props.dataItem.itemValue
                      const formatted = new Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: 'GBP'
                      }).format(value || 0)
                      return <td>{formatted}</td>
                    }}
                  />
                  <GridColumn 
                    field="vatValue" 
                    title="VAT" 
                    width="120px"
                    cell={(props) => {
                      const vat = props.dataItem.vatValue
                      const formatted = new Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: 'GBP'
                      }).format(vat || 0)
                      return <td>{formatted}</td>
                    }}
                  />
                  <GridColumn 
                    field="invoiceId" 
                    title="Actions" 
                    width="150px"
                    cell={(props) => (
                      <td>
                        <Button
                          onClick={() => window.open(`/invoicing/?id=${props.dataItem.invoiceId}`, '_blank')}
                          themeColor="primary"
                        >
                          View Invoice
                        </Button>
                      </td>
                    )}
                  />
                </Grid>
              )}
            </div>
          </TabStripTab>
        </TabStrip>
      </div>
    </div>
  )
}

export default FinancialStatement
